import React from 'react';
import { FormikProps } from 'formik';
import { StyledInputPrefix } from '@apps/registry/common/components/AddCashFund/AddCashFundDialog.styles';
import { AddCashFundDialogFields } from '@apps/registry/common/components/AddCashFund/types';
import { DonationFundPlatformTypeEnum } from '@graphql/generated';
import { SelectV1, InputV2, LinkV2, FormControl, Flex } from '@withjoy/joykit';
import { paymentMethodOptions, PaymentMethodOption } from '@shared/utils/paymentMethodOptions';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { getPaymentUsernameFormat } from '@apps/registry/common/components/AddCashFund/helpers';

export interface AppPlatformSelectProps {
  formik: FormikProps<AddCashFundDialogFields>;
  isMobile: boolean;
  setPaymentMethod: (paymentMethod?: DonationFundPlatformTypeEnum) => void;
  selectedPaymentMethod: Maybe<PaymentMethodOption>;
  paymentLink: string;
  onUsernameFocus: React.ChangeEventHandler<HTMLInputElement>;
  isEditDialog?: boolean;
}

const localPaymentOptions = paymentMethodOptions.filter(method => method.value !== 'cashOrCheck' && method.value !== 'joyCredit');

export const AppPlatformSelect: React.FC<AppPlatformSelectProps> = props => {
  const { formik, isMobile, setPaymentMethod, selectedPaymentMethod, paymentLink, onUsernameFocus, isEditDialog } = props;
  const errors = formik.errors;

  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();

  const { paymentMethodLabel } = getCashFundDialogTranslations();
  return (
    <Flex
      alignItems="flex-start"
      justifyContent="center"
      flexDirection={['column', 'column', isEditDialog ? 'column' : 'row']}
      rowGap={4}
      columnGap={4}
    >
      <FormControl
        isInvalid={!!formik.touched.paymentMethod && !!errors.paymentMethod}
        error={formik.touched.paymentMethod && errors.paymentMethod ? errors.paymentMethod : undefined}
        width={['100%', '100%', isEditDialog ? '100%' : '200px']}
      >
        <>
          <SelectV1
            placeholder={paymentMethodLabel}
            searchable={false}
            isInvalid={!!formik.touched.paymentMethod && !!errors.paymentMethod}
            onChange={e => setPaymentMethod(e?.value as DonationFundPlatformTypeEnum)}
            options={localPaymentOptions}
            value={selectedPaymentMethod}
          />
          {isEditDialog && (
            <LinkV2
              href={paymentLink}
              target="_blank"
              style={{
                fontSize: '13px',
                lineHeight: '20px',
                opacity: formik.values.username ? 1 : 0,
                display: !!(errors.username && (formik.touched.paymentMethod || formik.touched.username)) ? 'none' : 'block',
              }}
              tabIndex={0}
            >
              {paymentLink}
            </LinkV2>
          )}
        </>
      </FormControl>
      {(!isMobile || formik.values.paymentMethod || isEditDialog) && (
        <FormControl
          error={formik.touched.username && errors.username ? errors.username : undefined}
          isInvalid={!!formik.touched.username && !!errors.username}
          marginBottom={isEditDialog ? 0 : [6, null, 3]}
          width={['100%', '100%', isEditDialog ? '100%' : '50%']}
        >
          <>
            <Flex
              alignItems="center"
              overflow="hidden"
              position="relative"
              width={['100%', 'unset', 'unset']}
            >
              <InputV2
                {...formik.getFieldProps('username')}
                placeholder="username"
                tabIndex={0}
                style={{ padding: '0 16px 0 28px' }}
                onFocus={onUsernameFocus}
              />
              <StyledInputPrefix
                isFixedPrefix
                prefixOffset={15}
              >
                {getPaymentUsernameFormat(formik.values.paymentMethod, formik.values.currencyCode)}
              </StyledInputPrefix>
            </Flex>
            {!isEditDialog && formik.values.username && !formik.errors.username ? (
              <LinkV2
                href={paymentLink}
                target="_blank"
                style={{ fontSize: '13px' }}
              >
                {paymentLink}
              </LinkV2>
            ) : (
              <></>
            )}
          </>
        </FormControl>
      )}
    </Flex>
  );
};
